<script lang="ts">
  // Custom station sheet implementation using BJL Button and Spinner.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors, getRouteColor, routeBadgeLabel } from '../../ui/colors'
  import {
    bulletFontPx,
    bulletText,
    cardChrome,
    panelFrame,
    ROW_RULE,
    Type,
  } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { formatCountdown, formatShortClock as formatClock } from '../../ui/transitTime'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import { useTransitStore } from '../../store/transit'
  import { useFavoritesStore } from '../../store/favorites'
  import { api, type StationExit, type StopArrival } from '../../services/api'
  import { storeSelect } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import { dedupeExits, hasElevator, titleCase, DIAGRAM } from './stationExits'
  import type { TransferContext } from './timetable'
  import type { StationInfo } from './stationInfo'
  import StationPlatformDiagram from './StationPlatformDiagram.svelte'
  import TripTimetable from './TripTimetable.svelte'
  import TransferBanner from './TransferBanner.svelte'
  import RouteBadge from '../RouteBadge.svelte'
  import SectionEyebrow from '../SectionEyebrow.svelte'
  import SheetBackButton from '../SheetBackButton.svelte'

  interface Props {
    station: StationInfo
    onClose: () => void
    /** Returns to nearby-stops list when opened from it. */
    onBack?: () => void
    /** Handles station transfer target selection. */
    onTransfer?: (stopId: string, stopName: string, ctx: TransferContext | null) => void
    /** Active transfer context. */
    transfer?: TransferContext | null
  }

  const { station, onClose, onBack, onTransfer, transfer }: Props = $props()

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const SHEET_MIN = 80
  const SHEET_DEFAULT = Math.round(_wh * 0.45)
  const SHEET_MAX = Math.round(_wh * 0.92)

  const TIMES_PER_ROUTE = 30 // Max arrival times per route
  const CARD_TIMES = 2 // Number of arrival times shown on summary card

  function formatMin(ts: number): string {
    return formatCountdown(ts, { includeUnit: false })
  }

  const selectedRouteIds = storeSelect(useTransitStore, (s) => s.selectedRouteIds)
  // Selecting the boolean rather than the Set means the star re-renders only when THIS
  // stop's status flips, not on every favorites change.
  const isFavorited = storeSelect(useFavoritesStore, (s) => s.favorites.has(station.id))

  let details = $state<{ exits: StationExit[]; lat: number; lon: number } | null>(null)
  let arrivals = $state<StopArrival[] | null>(null)
  let loadingDetails = $state(true)
  let loadingArrivals = $state(true)

  let selectedTrip = $state<{ tripId: string; routeId: string } | null>(null) // Selected trip detail
  let selectedLine = $state<{ routeId: string; direction: string } | null>(null) // Selected line detail
  let tripMeta = $state<string | null>(null) // Subtitle for trip timetable

  // Keyed off the id, not the object: a host that rebuilds it each poll would otherwise
  // restart both fetches.
  const stationId = $derived(station.id)

  $effect(() => {
    stationId
    selectedTrip = null
    selectedLine = null
    tripMeta = null
  })

  // A different train is a different journey, so clear the subtitle rather than showing the
  // previous destination until the new fetch lands.
  $effect(() => {
    selectedTrip
    tripMeta = null
  })

  // Android Back button handlers for drilldown levels.
  useBackHandler(() => selectedLine !== null, () => { selectedLine = null })
  useBackHandler(() => selectedTrip !== null, () => { selectedTrip = null })

  // Scroll position reset on drilldown navigation.
  let bodyEl = $state<HTMLDivElement | null>(null)
  const drillKey = $derived(
    `${stationId}|` +
      (selectedTrip
        ? `trip:${selectedTrip.tripId}`
        : selectedLine
          ? `line:${selectedLine.routeId}:${selectedLine.direction}`
          : 'overview'),
  )
  $effect(() => {
    drillKey
    if (bodyEl) bodyEl.scrollTop = 0
  })

  // Resize
  const sheet = useResizableSheet({
    minHeight: SHEET_MIN,
    defaultHeight: SHEET_DEFAULT,
    maxHeight: SHEET_MAX,
  })

  $effect(() => {
    const id = stationId
    let cancelled = false
    loadingDetails = true
    api
      .getStationDetails(id)
      .then((d) => {
        if (cancelled) return
        details = { exits: d.exits, lat: d.lat, lon: d.lon }
        loadingDetails = false
      })
      .catch(() => {
        if (cancelled) return
        details = { exits: [], lat: 0, lon: 0 }
        loadingDetails = false
      })
    return () => { cancelled = true }
  })

  $effect(() => {
    const id = stationId
    let live = true
    const fetchArrivals = () => {
      api
        .getStopArrivals(id, TIMES_PER_ROUTE)
        .then((d) => { if (live) arrivals = d.arrivals })
        .catch(() => { if (live) arrivals = [] })
        .finally(() => { if (live) loadingArrivals = false })
    }
    loadingArrivals = true
    fetchArrivals()
    const timer = setInterval(fetchArrivals, 30_000)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  const accessible = $derived(details ? hasElevator(details.exits) : false)
  const exits = $derived(details ? dedupeExits(details.exits) : [])

  // Formats up to three main entrance locations.
  const exitSummary = $derived.by(() => {
    const named = exits.filter((e) => e.street || e.corner)
    if (named.length === 0) return null
    const rank = (e: StationExit) => (e.name.toLowerCase().includes('elevator') ? 0 : 1)
    return [...named]
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 3)
      .map((e) => `${titleCase(e.name)} at ${titleCase(e.street || e.corner || '')}`)
      .join(' · ')
  })

  // A transferring rider can override the filter; a fresh transfer re-arms it.
  let showAll = $state(false)
  $effect(() => {
    transfer
    showAll = false
  })
  const cutoff = $derived(transfer && !showAll ? transfer.arriveAt : null)

  // Filtered arrivals matching active transfer arrival time.
  const visible = $derived.by(() => {
    if (!arrivals || cutoff == null) return arrivals
    return arrivals
      .map((a) => ({ ...a, times: a.times.filter((t) => t.time >= cutoff) }))
      .filter((a) => a.times.length > 0)
  })

  // By GTFS direction, for the Uptown/Downtown headings.
  const uptown = $derived(visible?.filter((a) => a.direction === 'N') ?? [])
  const downtown = $derived(visible?.filter((a) => a.direction === 'S') ?? [])
  const other = $derived(
    visible?.filter((a) => a.direction !== 'N' && a.direction !== 'S') ?? [],
  )

  // Re-resolved from the live list each poll rather than snapshotted at tap time, so an open
  // schedule keeps counting down and drops trains as they depart.
  const scheduleGroup = $derived.by(() => {
    if (!selectedLine) return null
    return (
      visible?.find(
        (a) => a.routeId === selectedLine!.routeId && a.direction === selectedLine!.direction,
      ) ?? {
        routeId: selectedLine.routeId,
        direction: selectedLine.direction as StopArrival['direction'],
        times: [],
      }
    )
  })
  const directionLabel = $derived(
    selectedLine?.direction === 'N' ? 'Uptown' : selectedLine?.direction === 'S' ? 'Downtown' : '',
  )

  // Drilled in, the header collapses from the signboard to a compact back row: the screen
  // is no longer about the station.
  const drilled = $derived(selectedTrip !== null || selectedLine !== null)

  function selectTrip(tripId: string, routeId: string) {
    selectedTrip = { tripId, routeId }
  }
  function selectLine(a: StopArrival) {
    selectedLine = { routeId: a.routeId, direction: a.direction }
  }
</script>

<!-- Route bullet snippet -->
{#snippet routeBullet(routeId: string, size = 32)}
  <RouteBadge {routeId} {size} />
{/snippet}

{#snippet eyebrow(text: string, marginBottom: number)}
  <SectionEyebrow {text} {marginBottom} />
{/snippet}

<!-- Back button snippet. Size and touch target are `BACK_ARROW_CHROME`, shared with every
     other sheet that has levels; the negative margin is this sheet's own, pulling the wider
     box back onto the title's left edge. -->
{#snippet backButton(onclick: () => void, label: string)}
  <SheetBackButton {onclick} {label} style="margin-left: -6px;" />
{/snippet}

<!-- Direction arrivals card snippet -->
{#snippet directionCard(label: string, list: StopArrival[], clock: boolean)}
  <div style="{cardChrome()} padding: 14px; min-width: 0;">
    {@render eyebrow(label, 0)}
    {#each list as a, i (`${a.routeId}:${i}`)}
      {@const times = a.times}
      {@const shown = times.slice(0, CARD_TIMES)}
      <button
        onclick={() => selectLine(a)}
        aria-label="See the next hour of {a.routeId} trains {label.toLowerCase()} at this station"
        style="
          display: block; width: 100%; text-align: left; cursor: pointer;
          background: none; border: none; padding: 0;
          margin-top: 12px;
          {i > 0 ? `border-top: ${ROW_RULE}; padding-top: 12px;` : ''}
        "
      >
        <div style="display: flex; align-items: baseline; gap: 8px; min-width: 0;">
          <span style="align-self: center; flex-shrink: 0;">
            {@render routeBullet(a.routeId, 24)}
          </span>
          {#if shown[0]}
            {@const first = clock ? formatClock(shown[0].time) : formatMin(shown[0].time)}
            {@const isNow = !clock && first === 'Now'}
            <span style="color: {isNow ? Colors.success : Colors.text}; {Type.screen}; min-width: 0;">
              {first}
            </span>
            {#if !clock && !isNow}
              <span style="color: {Colors.textSecondary}; {Type.caption};">min</span>
            {/if}
          {:else}
            <span style="color: {Colors.textMuted}; {Type.body};">No trains</span>
          {/if}
        </div>
        {#if shown[1]}
          <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
            then {clock ? formatClock(shown[1].time) : `${formatMin(shown[1].time)} min`}
          </div>
        {/if}
      </button>
    {/each}
  </div>
{/snippet}

<!-- Per-route schedule snippet for next hour -->
{#snippet routeSchedule(arrival: StopArrival, arriveAt: number | null)}
  {#if arrival.times.length === 0}
    <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
      No more {arrival.routeId} trains reported in the next hour.
    </div>
  {:else}
    {@render eyebrow(
      arriveAt != null
        ? 'After you arrive · tap a train for its stops'
        : 'Next hour · tap a train for its stops',
      2,
    )}

    <div style="display: flex; flex-direction: column;">
      {#each arrival.times as t, i (`${t.tripId}:${i}`)}
        {@const wait = arriveAt != null ? Math.max(0, Math.round((t.time - arriveAt) / 60)) : null}
        {@const label = formatMin(t.time)}
        {@const isNow = arriveAt == null && label === 'Now'}
        <button
          onclick={() => selectTrip(t.tripId, arrival.routeId)}
          style="
            display: flex; align-items: center; gap: 10px; padding: 10px 0;
            background: none; border: none; border-bottom: 1px solid {Colors.border};
            cursor: pointer; text-align: left; width: 100%;
          "
        >
          <div
            style="
              width: 8px; height: 8px; border-radius: 4px; flex-shrink: 0;
              background-color: {getRouteColor(arrival.routeId)}; opacity: {isNow ? 1 : 0.55};
            "
          ></div>
          <span style="flex: 1; min-width: 0; color: {Colors.text}; {Type.bodyLg}; font-weight: 700;">
            {formatClock(t.time)}
          </span>
          <span
            style="
              color: {isNow ? Colors.success : Colors.textSecondary};
              {Type.body}; font-weight: {isNow ? 800 : 700}; flex-shrink: 0;
            "
          >
            {wait != null ? (wait === 0 ? 'no wait' : `${wait} min wait`) : isNow ? 'Now' : `${label} min`}
          </span>
          <span style="color: {Colors.textSecondary}; font-size: 15px; flex-shrink: 0;">›</span>
        </button>
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet diagramLegend(list: StationExit[])}
  {@const hasElev = list.some((e) => e.name.toLowerCase().includes('elevator'))}
  {@const hasEsc = list.some((e) => e.name.toLowerCase().includes('escalator'))}
  {@const entries = [
    { color: Colors.text, label: 'Stair' },
    ...(hasElev ? [{ color: Colors.primary, label: 'Elevator' }] : []),
    ...(hasEsc ? [{ color: Colors.success, label: 'Escalator' }] : []),
  ]}
  <div style="display: flex; gap: 14px; margin-top: 6px; padding-left: 2px;">
    {#each entries as { color, label } (label)}
      <div style="display: flex; align-items: center; gap: 4px;">
        <div
          style="
            width: 8px; height: 8px; border-radius: 4px; flex-shrink: 0;
            border: 1.5px solid {color}; background-color: {DIAGRAM.dotFill};
          "
        ></div>
        <span style="color: {Colors.textMuted}; {Type.caption};">{label}</span>
      </div>
    {/each}
  </div>
{/snippet}

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    height: {sheet.height}px;
    background-color: {Colors.ground};
    {panelFrame('top')}
    z-index: 1000;
    display: flex; flex-direction: column;
    transition: {sheet.dragging ? 'none' : 'height 0.22s cubic-bezier(0.4,0,0.2,1)'};
  "
>
  <!-- Top bar with back, favorite, close, and drag handle -->
  <div style="position: relative; flex-shrink: 0; display: flex; align-items: center; padding: 4px 14px 2px;">
    <!-- One back affordance, innermost level first: a train's timetable returns to whichever
         level opened it (a route's schedule, or the arrivals overview); at the overview it
         returns to the nearby-stops list, when that's where the sheet came from. -->
    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if selectedTrip}
        {@render backButton(
          () => { selectedTrip = null },
          selectedLine ? 'Back to this route’s schedule' : 'Back to station arrivals',
        )}
      {:else if selectedLine}
        {@render backButton(() => { selectedLine = null }, 'Back to station arrivals')}
      {:else if onBack}
        {@render backButton(onBack, 'Back to nearby stops')}
      {/if}
    </div>

    <!-- The drag zone is the empty span *between* the two control clusters. Wrapping the
         buttons in it instead would both invalidate the separator role and let a
         preventDefault'd pointerdown swallow their taps. -->
    <div
      onpointerdown={sheet.onHandleDown}
      role="separator"
      aria-label="Resize station panel"
      style="flex: 1; align-self: stretch; min-height: 34px; cursor: ns-resize; user-select: none; touch-action: none;"
    ></div>

    <!-- Centred on the sheet, not on the drag zone: the two clusters are different widths,
         so only absolute positioning keeps the grabber on the sheet's midline. -->
    <div
      style="
        position: absolute; left: 50%; top: 14px; transform: translateX(-50%);
        width: 36px; height: 4px; border-radius: 2px;
        background-color: rgba(255,255,255,0.25); pointer-events: none;
      "
    ></div>

    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if !drilled}
        <Button
          variant="text"
          size="sm"
          iconOnly
          onclick={() => useFavoritesStore.getState().toggleFavorite(station.id)}
          aria-label={isFavorited.value ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={isFavorited.value}
          title={isFavorited.value ? 'Remove from favorites' : 'Add to favorites'}
          style="color: {isFavorited.value ? Colors.warning : Colors.textSecondary};"
        >{isFavorited.value ? '★' : '☆'}</Button>
      {/if}
      <!-- Pulled 9px left. The visible gap between ★ and ✕ is nothing but the two buttons'
           own 18px side padding (36px total); -9px trims that by a quarter without shrinking
           either tap target. Nothing to trim when ✕ stands alone. -->
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={onClose}
        aria-label="Close station panel"
        style={drilled ? '' : 'margin-left: -9px;'}
      >✕</Button>
    </div>
  </div>

  <!-- Scrollable body -->
  <div
    bind:this={bodyEl}
    style="overflow-y: auto; flex: 1; min-height: 0; padding: 0 16px {safeBottom(28)};"
  >
    {#if selectedTrip}
      <!-- Train timetable detail view -->
      <div style="display: flex; align-items: center; gap: 12px; padding: 4px 0 12px;">
        {@render routeBullet(selectedTrip.routeId)}
        <div style="min-width: 0;">
          <div style="color: {Colors.text}; {Type.heading};">Arriving train</div>
          {#if tripMeta}
            <div
              style="
                color: {Colors.textSecondary}; {Type.caption};
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
              "
            >{tripMeta}</div>
          {/if}
        </div>
      </div>
      <TripTimetable
        source={{ kind: 'subway', tripId: selectedTrip.tripId }}
        routeId={selectedTrip.routeId}
        currentStopId={station.id}
        onTransfer={(stopId, stopName, ctx) => onTransfer?.(stopId, stopName, ctx)}
        onMetaLoaded={(destination, origin) => {
          tripMeta = [destination && `to ${destination}`, origin && `from ${origin}`]
            .filter(Boolean)
            .join(' · ') || null
        }}
      />
    {:else if scheduleGroup}
      <!-- Route schedule detail view -->
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 10px;">
        {@render routeBullet(scheduleGroup.routeId)}
        <div style="min-width: 0;">
          <div style="color: {Colors.text}; {Type.title};">
            {directionLabel
              ? `${directionLabel} ${scheduleGroup.routeId} trains`
              : `${scheduleGroup.routeId} trains`}
          </div>
          <!-- Which station these times are for. The train view gets this from `tripMeta`
               ("from City Hall"); a bare list of clock times would otherwise be the one
               screen in the drill-down that never names where you're standing. -->
          <div
            style="
              color: {Colors.textSecondary}; {Type.caption};
              overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            "
          >at {station.name}</div>
        </div>
      </div>
      {@render routeSchedule(scheduleGroup, cutoff)}
    {:else}
      <!-- Station signboard header -->
      <div style="margin: 10px 4px 0; border: 2px solid {Colors.text}; padding: 12px 14px;">
        <div style="display: flex; align-items: flex-start; gap: 10px;">
          <span style="flex: 1; min-width: 0; color: {Colors.text}; {Type.display};">
            {station.name}
          </span>
          {#if accessible}
            <!-- Station fact, not a control, so it reads with the name rather than with the
                 pinned ★/✕. 3px drops it onto the first line's optical centre. -->
            <span
              style="font-size: 16px; line-height: 1; margin-top: 3px; flex-shrink: 0;"
              title="Elevator access"
            >♿</span>
          {/if}
        </div>

        <!-- Route bullet filters -->
        <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 7px; margin-top: 8px;">
          {#each station.routes as routeId (routeId)}
            {@const color = getRouteColor(routeId)}
            {@const active = selectedRouteIds.value.has(routeId)}
            {@const express = routeId.includes('X')}
            {@const label = routeBadgeLabel(routeId)}
            <button
              onclick={() => useTransitStore.getState().toggleSelectedRoute(routeId)}
              aria-label="{active ? 'Stop filtering' : 'Filter'} the map to the {label} line"
              style="
                background: none; border: none; padding: 0; cursor: pointer;
                box-shadow: {active ? `0 0 0 2px ${Colors.text}` : 'none'};
                border-radius: {express ? '3px' : '14px'};
                transition: box-shadow 0.15s; flex-shrink: 0;
              "
            >
              <RouteBadge {routeId} size={28} />
            </button>
          {/each}
        </div>

        <!-- Entrance summary -->
        {#if exitSummary}
          <div
            style="
              border-top: 1px solid rgba(255,255,255,0.4); margin-top: 10px;
              padding-top: 8px; color: {Colors.textSecondary}; {Type.body};
            "
          >{exitSummary}</div>
        {/if}
      </div>

      <!-- Station layout diagram -->
      {#if loadingDetails}
        <div
          style="
            {cardChrome()} height: 92px; margin-top: 16px;
            display: flex; align-items: center; justify-content: center;
          "
        >
          <span style="display: flex; align-items: center; gap: 8px; color: {Colors.textMuted}; {Type.body};">
            <Spinner size="sm" label="Loading platform map" /> Loading platform map…
          </span>
        </div>
      {:else if details && details.exits.length > 0}
        <div style="{cardChrome()} padding: 16px; margin-top: 16px;">
          {@render eyebrow('Station layout', 14)}
          <StationPlatformDiagram {exits} stationLat={details.lat} stationLon={details.lon} />
          {@render diagramLegend(exits)}
        </div>
      {/if}

      {#if transfer}
        <TransferBanner ctx={transfer} active={!showAll} onToggle={() => { showAll = !showAll }} />
      {/if}

      {#if loadingArrivals}
        <div style="display: flex; align-items: center; gap: 8px; padding: 16px 0; color: {Colors.textMuted}; {Type.body};">
          <Spinner size="sm" label="Loading arrivals" /> Loading…
        </div>
      {:else if visible && visible.length > 0}
        <!-- Direction arrival cards -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px;">
          {#if uptown.length > 0}{@render directionCard('Uptown', uptown, cutoff != null)}{/if}
          {#if downtown.length > 0}{@render directionCard('Downtown', downtown, cutoff != null)}{/if}
        </div>
        {#if other.length > 0}
          <div style="margin-top: 12px;">
            {@render directionCard('Other', other, cutoff != null)}
          </div>
        {/if}
      {:else if cutoff != null}
        <!-- Filtered to nothing. Usually not a service gap: the real-time index only reaches
             about an hour ahead, so a transfer far out simply has no departures published
             yet. Say so, rather than implying the trains have stopped. -->
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          Nothing published yet for after {formatClock(cutoff)}. Real-time arrivals only reach
          about an hour ahead: check again as you get closer, or tap “Show all” to see what’s
          running now.
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          No real-time arrivals available.
        </div>
      {/if}
    {/if}
  </div>
</div>
